import React from 'react';
import FileSaver from 'file-saver';
import { GetOrderIntentEstimateRequestForPartnerOrderReview } from '@atlassian/cssxp-typescommon';
import { getOrderEstimateForPartnerOrderReview } from '@atlassian/temp-proxies';
import { BillEstimateOrderItemDto } from '@atlassian/cssxp-ccp-types';
import { updateAdjustmentsToPartnerDiscounts } from '@css-xp/ppc-utils';
import { NotificationTypes, successNotificationBuilder, useNotification } from '@css-xp/base';
import { CheckoutOperation, usePartnerCheckoutNotification } from './use-place-order-notification';

export const useEstimateJsonDownload = (): {
  isEstimateJsonIsDownloading: boolean;
  prepareAndSaveJson: (params: {
    intentEstimateRequestPayload: GetOrderIntentEstimateRequestForPartnerOrderReview;
    onDownloadComplete?: () => void;
  }) => Promise<void>;
} => {
  const [isEstimateJsonIsDownloading, setIsEstimateJsonIsDownloading] = React.useState(false);

  const { notifyFailure } = usePartnerCheckoutNotification();
  const { notificationSent, sendSuccessNotification, setNotificationSent } = useNotification();

  const prepareAndSaveJson = async ({
    intentEstimateRequestPayload,
    onDownloadComplete,
  }: {
    intentEstimateRequestPayload: GetOrderIntentEstimateRequestForPartnerOrderReview;
    onDownloadComplete?: () => void;
  }) => {
    setIsEstimateJsonIsDownloading(true);
    try {
      if (!notificationSent) {
        const notification = successNotificationBuilder()
          .isVersion(NotificationTypes.FLAG)
          .withHeader('Your estimate JSON is loading')
          .withMessage('This will just take a moment')
          .withDismissAfter(3000)
          .build();
        sendSuccessNotification(notification);
        setNotificationSent(true);
      }

      const response = await getOrderEstimateForPartnerOrderReview(intentEstimateRequestPayload);
      response?.items?.forEach((item) => {
        // we don't get entitlementId in the response from bill estimate api because we always send with creation order, so we need to add it from the request payload
        if (!item.entitlementId) {
          const entitlementObj = (obj: any) => obj?.entitlementNumber && obj.itemId === item.itemId;
          // @ts-ignore - adding this type error-ignore as entitlementId is not part of the type never
          item.entitlementId = intentEstimateRequestPayload?.items?.find(entitlementObj)?.entitlementId ?? null; //we don't get entitlement id for net new products. so adding default value as null
        }
      });
      if (response && response?.items) {
        updateAdjustmentsToPartnerDiscounts(response.items as BillEstimateOrderItemDto[]);
      }
      const jsonString = JSON.stringify(response, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      FileSaver.saveAs(blob, `Order-Estimate-${intentEstimateRequestPayload.orderIntentId}.json`);
      onDownloadComplete && onDownloadComplete();
    } catch {
      notifyFailure('Could not download JSON. Please try again in some time', CheckoutOperation.Others);
    } finally {
      setIsEstimateJsonIsDownloading(false);
    }
  };

  return {
    isEstimateJsonIsDownloading,
    prepareAndSaveJson,
  };
};
